import {
  AlertTriangle,
  ClipboardList,
  RefreshCw,
  Settings,
  X,
} from 'lucide-react';
import type { useTruthUtilisationPanel } from '../../hooks/useTruthUtilisationPanel';
import type { TruthUtilisationJobRecord } from '../../types/utilisationTruth';
import { Badge, type BadgeTone } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

type PanelState = ReturnType<typeof useTruthUtilisationPanel>;

interface TruthUtilisationPipelinePanelProps {
  panel: PanelState;
  compact?: boolean;
  className?: string;
  /** Optional utilisation assumptions control (new row under Compute / Jobs). */
  assumptionsActive?: boolean;
  onAssumptionsClick?: () => void;
}

function JobRow({ job }: { job: TruthUtilisationJobRecord }) {
  const statusTone: BadgeTone =
    job.status === 'completed'
      ? job.recordCount === 0
        ? 'warning'
        : 'success'
      : job.status === 'failed'
        ? 'destructive'
        : job.status === 'running'
          ? 'info'
          : 'neutral';

  return (
    <div className="px-3 py-2 border-b border-border/50 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Badge tone={statusTone}>
            {job.status}
          </Badge>
          <span className="text-xs text-foreground font-mono">
            {job.startDate} → {job.endDate}
          </span>
          {job.recordCount != null && (
            <span className="text-xs text-muted-foreground">{job.recordCount.toLocaleString()} records</span>
          )}
        </div>
        <span className="text-xs text-accent font-mono shrink-0">
          {new Date(job.createdAt).toLocaleString()}
        </span>
      </div>
      {job.status === 'failed' && job.error && (
        <p className="mt-1 text-xs text-destructive break-all">{job.error}</p>
      )}
      {job.status === 'completed' && job.recordCount === 0 && (
        <p className="mt-1 text-xs text-warning">
          Completed with 0 vehicle-days — Util % will stay empty. Run profitability compute for this
          same date range, then Run Again.
        </p>
      )}
      {job.status === 'pending' && (
        <p className="mt-1 text-xs text-muted-foreground">
          Waiting for utilisation to finish computing. If this does not change within a minute,
          try again later or contact your ZeeFleet administrator.
        </p>
      )}
    </div>
  );
}

/**
 * Utilisation truth projection: compute + job status.
 * Run profitability compute for the same range before utilisation (revenue component).
 */
export default function TruthUtilisationPipelinePanel({
  panel,
  compact = false,
  className = '',
  assumptionsActive = false,
  onAssumptionsClick,
}: TruthUtilisationPipelinePanelProps) {
  const {
    statusData,
    statusLoading,
    statusError,
    lastComputed,
    computeJobs,
    computeJobsLoading,
    computeTriggerLoading,
    computeTriggerError,
    recomputeWarning,
    showJobsPanel,
    setShowJobsPanel,
    hasActiveJob,
    conflictingCompletedJob,
    handleCompute,
    doCompute,
    setRecomputeWarning,
    refreshJobs,
  } = panel;

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        className={`rounded-lg border border-chart-10/30 bg-chart-10/10 ${
          compact ? 'p-2.5' : 'p-3'
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className={`font-medium text-chart-10 ${compact ? 'text-xs' : 'text-sm'}`}>
              Utilisation projection
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Weighted utilisation score (mileage, time, revenue). Run profitability compute first, then compute utilisation separately for the same dates.
            </p>
            {statusLoading && (
              <p className="text-xs text-muted-foreground mt-1 animate-pulse">Checking status…</p>
            )}
            {statusError && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {statusError}
              </p>
            )}
            {statusData?.active_job && (
              <p className="text-xs text-ring mt-1 flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin shrink-0" />
                Job {statusData.active_job.status}: {statusData.active_job.startDate} →{' '}
                {statusData.active_job.endDate}
              </p>
            )}
            {lastComputed.computedAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Last computed: {new Date(lastComputed.computedAt).toLocaleString()}
                {lastComputed.recordCount != null && ` (${lastComputed.recordCount} vehicle-days)`}
              </p>
            )}
            {!lastComputed.computedAt && statusData && !statusLoading && (
              <p className="text-xs text-warning mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                No utilisation projection for this period — run Compute.
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleCompute}
                disabled={computeTriggerLoading}
                className="bg-chart-10 hover:bg-chart-10 disabled:bg-muted disabled:opacity-100"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${computeTriggerLoading ? 'animate-spin' : ''}`} />
                {computeTriggerLoading ? 'Queuing…' : 'Compute utilisation'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowJobsPanel((v) => !v)}
                className="relative border border-border"
              >
                <ClipboardList className="w-3.5 h-3.5 text-chart-10" />
                Jobs
                {hasActiveJob && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-chart-10 animate-pulse" />
                )}
              </Button>
            </div>
            {onAssumptionsClick ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onAssumptionsClick}
                className={cn(
                  'border',
                  assumptionsActive
                    ? 'bg-warning/20 border-warning/50 text-warning hover:bg-warning/20'
                    : 'border-border hover:border-warning'
                )}
                title="Edit utilisation targets and formula weights"
              >
                <Settings className="w-3.5 h-3.5 text-warning" />
                Assumptions
              </Button>
            ) : null}
          </div>
        </div>

        {statusData && statusData.warnings.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {statusData.warnings.map((w, i) => (
              <span key={i} className="text-xs text-warning flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {w}
              </span>
            ))}
          </div>
        )}
      </div>

      {recomputeWarning && conflictingCompletedJob && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-warning/20 border border-warning/40 rounded-lg text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 text-warning" />
          <span className="text-warning flex-1">
            A completed utilisation job exists for {conflictingCompletedJob.startDate} →{' '}
            {conflictingCompletedJob.endDate}. Run again?
          </span>
          <Button
            type="button"
            variant="warning"
            onClick={() => void doCompute()}
            disabled={computeTriggerLoading}
            className="h-auto px-2 py-1 text-white"
          >
            Run Again
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRecomputeWarning(false)}
            className="h-auto px-2 py-1"
          >
            Cancel
          </Button>
        </div>
      )}

      {showJobsPanel && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-secondary/50 border-b border-border">
            <span className="text-xs font-semibold text-foreground">Utilisation jobs</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => void refreshJobs()}
                className="h-auto px-0 text-xs"
              >
                {computeJobsLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => setShowJobsPanel(false)}
                className="p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          {computeTriggerError && (
            <div className="px-3 py-2 bg-destructive/20 border-b border-destructive/30 text-xs text-destructive">
              {computeTriggerError}
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {computeJobs.length === 0 && !computeJobsLoading && (
              <p className="px-3 py-4 text-xs text-muted-foreground text-center">
                No utilisation jobs yet. Click Compute utilisation to queue this period.
              </p>
            )}
            {computeJobs.map((job) => (
              <JobRow key={job.jobId} job={job} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
